import { FC, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
    Box,
    Button,
    Alert,
    AlertTitle,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Typography,
    Divider,
    Container,
    Link,
} from "@material-ui/core";
import InputField from "src/components/ui-components/InputField";
import useIsMountedRef from "../../../hooks/useIsMountedRef";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { GenerateForgotPasswordOTP, ValidateForgotPasswordOTP } from "./ConnectionService";
import AlertMessage from "src/components/ui-components/AlertMessage";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router";
import { Link as RouterLink } from 'react-router-dom';

const ForgotPassword: FC = (props) => {
    const ref = useRef(null);
    const isMountedRef = useIsMountedRef();
    const navigate = useNavigate();
    const location = useLocation();
    const { username, email } = location.state || {};
    const [canResend, setCanResend] = useState(false);
    const [resendTimer, setResendTimer] = useState(300);
    const [showPassword, setShowPassword] = useState({
        otp: false,
        password: false,
        confirmPassword: false,
    });

    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);

            return () => clearInterval(timer);
        } else {
            setCanResend(true);
        }
    }, [resendTimer]);

    const handleSavePassword = async (username: string, otp: string, password: string) => {
        try {
            const res = await ValidateForgotPasswordOTP(otp, username, password);
            if (res.statusCode === 200) {
                AlertMessage({ type: 'success', msg: res.message });
                navigate('/authentication/login');
            } else {
                AlertMessage({ type: 'error', msg: res.message });
            }
        } catch (err) {
            Object.entries(err.errors).forEach(([, errMsg]: any) => {
                AlertMessage({ type: 'error', msg: errMsg.errorMessage });
            });
        }
    };

    const handleResendOTP = async (e) => {
        e.preventDefault();  // Prevent form submission
        if (username && username.trim() !== '') {
            try {
                const res = await GenerateForgotPasswordOTP(username);
                if (res.statusCode === 200) {
                    AlertMessage({ type: 'success', msg: 'OTP resent successfully' });
                    setCanResend(false);
                    setResendTimer(300); // Reset timer
                } else {
                    AlertMessage({ type: 'error', msg: res.message });
                }
            } catch (err) {
                AlertMessage({ type: 'error', msg: 'Failed to resend OTP' });
            }
        } else {
            AlertMessage({ type: 'error', msg: 'Username is required to resend OTP' });
        }
    };

    const handleKeyUp = (e) => {
        if (e.keyCode === 13) {
            ref.current.handleSubmit();
        }
    };

    return (
        <>
            <Helmet>
                <title>Reset Password - Globatech</title>
            </Helmet>
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh'
                }}
            >
                <Container maxWidth="xs" sx={{ py: '80px' }}>
                    <Card sx={{ overflow: 'visible' }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box>
                                <Typography
                                    color="textPrimary"
                                    gutterBottom
                                    variant="h5"
                                >
                                    Reset Password
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box>
                                <Formik
                                    innerRef={ref}
                                    initialValues={{
                                        username: username || "",
                                        otp: "",
                                        password: "",
                                        confirmPassword: "",
                                        submit: null,
                                    }}
                                    validationSchema={Yup.object().shape({
                                        username: Yup.string().required("Username is required"),
                                        otp: Yup.string().max(6).matches(/^[0-9]+$/, 'OTP should only contain numbers').required("OTP is required"),
                                        password: Yup.string()
                                            .required('Password is required')
                                            .min(8, 'Password must be at least 8 characters long')
                                            .max(20, 'Password must not exceed 20 characters')
                                            .matches(/[A-Z]/, 'Password must contain at least one uppercase letter (A-Z)')
                                            .matches(/[a-z]/, 'Password must contain at least one lowercase letter (a-z)')
                                            .matches(/[0-9]/, 'Password must contain at least one number (0-9)')
                                            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character (e.g., !, @, #, $, etc.)')
                                            .matches(/^\S*$/, 'Password must not contain any spaces')
                                            .test('no-sequences', 'Password must not contain sequences (e.g., 1234, abcd) or repeated characters (e.g., aaa)', value => {
                                                const hasSequence = /(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i.test(value);
                                                const hasRepeated = /(.)\1\1/.test(value);
                                                return !hasSequence && !hasRepeated;
                                            })
                                            .test('no-username-email', 'Password must not contain parts of the username or email', function(value) {
                                                if (!value) return true;
                                                if (!username || !email) return true;
                                                
                                                const lowerCaseValue = value.toLowerCase();
                                                const lowerCaseUsername = username.toLowerCase();
                                                const emailUsernamePart = email.split('@')[0].toLowerCase();
                                                
                                                const containsSubstring = (str, substrLength) => {
                                                    for (let i = 0; i <= str.length - substrLength; i++) {
                                                        if (lowerCaseValue.includes(str.slice(i, i + substrLength))) {
                                                            return true;
                                                        }
                                                    }
                                                    return false;
                                                };
                                                
                                                return !(containsSubstring(lowerCaseUsername, 5) || containsSubstring(emailUsernamePart, 5));
                                            }),
                                        confirmPassword: Yup.string()
                                            .oneOf([Yup.ref("password"), null], "Passwords must match")
                                            .required("Confirm Password is required"),
                                    })}
                                    onSubmit={async (
                                        values,
                                        { setErrors, setStatus, setSubmitting }
                                    ): Promise<void> => {
                                        try {
                                            await handleSavePassword(values.username, values.otp, values.password);
                                        } catch (err) {
                                            if (isMountedRef.current) {
                                                setStatus({ success: false });
                                                setErrors({
                                                    submit: err.errors?.[0].errorMessage || err.title || err.message,
                                                });
                                                setSubmitting(false);
                                            }
                                        }
                                    }
                                    }
                                >
                                    {({ errors, handleSubmit, isSubmitting }): JSX.Element => (
                                        <form noValidate onSubmit={handleSubmit} {...props} autoComplete="off">
                                            <InputField
                                                name="username"
                                                label="User Name"
                                                margin="normal"
                                                disabled
                                                value={username}
                                                autoFocus
                                            />
                                            <InputField
                                                name="otp"
                                                label="OTP"
                                                margin="normal"
                                                type={showPassword.otp ? "text" : "password"}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => {
                                                                    setShowPassword((prev) => ({ ...prev, otp: !prev.otp }));
                                                                }}
                                                            >
                                                                {showPassword.otp ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <InputField
                                                name="password"
                                                label="New Password"
                                                margin="normal"
                                                onKeyUp={handleKeyUp}
                                                onPaste={(e) => e.preventDefault()}
                                                type={showPassword.password ? "text" : "password"}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => {
                                                                    setShowPassword((prev) => ({ ...prev, password: !prev.password }));
                                                                }}
                                                            >
                                                                {showPassword.password ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <InputField
                                                name="confirmPassword"
                                                label="Confirm Password"
                                                margin="normal"
                                                onKeyUp={handleKeyUp}
                                                onPaste={(e) => e.preventDefault()}
                                                type={showPassword.confirmPassword ? "text" : "password"}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => {
                                                                    setShowPassword((prev) => ({ ...prev, confirmPassword: !prev.confirmPassword }));
                                                                }}
                                                            >
                                                                {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {errors.submit && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Alert severity="error">
                                                        <AlertTitle>Error</AlertTitle>
                                                        {errors.submit}
                                                    </Alert>
                                                </Box>
                                            )}
                                            <Box>
                                                <Typography
                                                    color="textPrimary"
                                                    gutterBottom
                                                    variant="body2"
                                                    sx={{ mt: 1, display: 'flex', alignItems: 'center' }}
                                                >
                                                    <Link
                                                        color="textSecondary"
                                                        component="button"
                                                        onClick={(e) => handleResendOTP(e)}
                                                        variant="body2"
                                                        disabled={!canResend}
                                                    >
                                                        Resend OTP
                                                    </Link>
                                                    {!canResend && <span>&nbsp;in {resendTimer} seconds</span>}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    sx={{
                                                        background: "linear-gradient(to bottom, #072f40, #072f40)",
                                                    }}
                                                    disabled={isSubmitting}
                                                    fullWidth
                                                    size="large"
                                                    type="submit"
                                                    variant="contained"
                                                >
                                                    Save
                                                    {isSubmitting && (
                                                        <CircularProgress
                                                            color="primary"
                                                            thickness={5}
                                                            size={20}
                                                            sx={{ ml: 1 }}
                                                        />
                                                    )}
                                                </Button>
                                            </Box>
                                        </form>
                                    )}
                                </Formik>
                            </Box>
                            <Typography color="textPrimary" gutterBottom variant="body2" sx={{ mt: 2 }}>
                                <Divider sx={{ my: 2 }} />
                                <Link color="textSecondary" component={RouterLink} to="/authentication/login" variant="body2">
                                    Go back to login page
                                </Link>
                            </Typography>
                        </CardContent>
                    </Card>
                </Container>
            </Box >
        </>
    );
};

export default ForgotPassword;
